import * as React from 'react';
import renderIcon, { IconName } from 'helpers/renderIcon';
import { useRef, useState, useEffect, useCallback } from 'react';
import { addURLSearchParams, deleteURLSearchParams, readURLSearchParams } from 'helpers/common';
import useDebounce from 'helpers/debounce';
import { withRouter } from 'react-router';
const classNames = require('classnames');

let firstLoad = true;

interface IProps {
	input?: any;
	className?: string;
	minValue: number;
	maxValue: number;
	onChange?: ({ min, max }: { min: number; max: number }) => void;
	iconName?: IconName;
	iconClassName?: string;
	iconAction?: () => void | undefined;
	customIconSizeWidth?: number;
	customIconSizeHeight?: number;
	customMinDisplayValueFunction?: (x: number) => string;
	customMaxDisplayValueFunction?: (x: number) => string;
	putSelectedValueInUrlQuery?: string; // url prop key name
	highlightWhenValueSet?: boolean;
	displayOnlyRangeSlider?: boolean;

	location?: any; // obtained with withRouter
	filterFrom?: number;
	filterTo?: number;
	resetAppliedFilters?: boolean;
	setResetAppliedFilters?: React.Dispatch<React.SetStateAction<boolean>>;
}

const RangeSliderFieldW = (props: IProps) => {
	const {
		className,
		customIconSizeHeight,
		customIconSizeWidth,
		iconClassName,
		iconAction,
		iconName,
		input,
		minValue,
		maxValue,
		onChange,
		customMinDisplayValueFunction,
		customMaxDisplayValueFunction,
		putSelectedValueInUrlQuery,
		highlightWhenValueSet,
		displayOnlyRangeSlider,
		filterFrom,
		filterTo,
		resetAppliedFilters,
		setResetAppliedFilters,
	} = props;
	const [open, setOpen] = useState(false);

	const [minVal, setMinVal] = useState(filterFrom ? filterFrom * 60 : minValue);
	const [maxVal, setMaxVal] = useState(filterTo ? filterTo * 60 : maxValue);
	const debouncedMinVal = useDebounce(minVal, 400);
	const debouncedMaxVal = useDebounce(maxVal, 400);
	const fieldRef = useRef<HTMLDivElement>(null);
	const minValRef = useRef(null);
	const maxValRef = useRef(null);
	const range = useRef(null);

	//////////////////////////////////////////////
	// Close dropdown if clicked outside
	//////////////////////////////////////////////
	const handleClick = (e) => {
		if (fieldRef.current && fieldRef.current.contains(e.target)) {
			return;
		} else {
			setOpen(false);
		}
	};
	useEffect(() => {
		document.addEventListener('click', handleClick);
		return () => {
			document.removeEventListener('click', handleClick);
		};
	}, []);
	//////////////////////////////////////////////
	// Apply values from URL query on mount - if any
	//////////////////////////////////////////////
	useEffect(() => {
		if (putSelectedValueInUrlQuery) {
			const extractedParamValueMin = readURLSearchParams(putSelectedValueInUrlQuery + 'Min');
			const extractedParamValueMax = readURLSearchParams(putSelectedValueInUrlQuery + 'Max');
			if (extractedParamValueMin) {
				setMinVal(Number(extractedParamValueMin));
			}
			if (extractedParamValueMax) {
				setMaxVal(Number(extractedParamValueMax));
			}
		}
	}, []);
	//////////////////////////////////////////////
	// Remove filters if they are removed from URL
	//////////////////////////////////////////////
	useEffect(() => {
		if (putSelectedValueInUrlQuery) {
			if (
				!readURLSearchParams(putSelectedValueInUrlQuery + 'Min') &&
				!readURLSearchParams(putSelectedValueInUrlQuery + 'Max')
			) {
				setMinVal(minValue);
				setMaxVal(maxValue);
			} else if (
				!readURLSearchParams(putSelectedValueInUrlQuery + 'Min') &&
				readURLSearchParams(putSelectedValueInUrlQuery + 'Max')
			) {
				setMinVal(minValue);
			} else if (
				!readURLSearchParams(putSelectedValueInUrlQuery + 'Max') &&
				readURLSearchParams(putSelectedValueInUrlQuery + 'Min')
			) {
				setMaxVal(maxValue);
			}
		}
	}, [props.location]);
	//////////////////////////////////////////////

	// Convert to percentage
	const getPercent = useCallback(
		(value) => {
			return Math.round(((value - minValue) / (maxValue - minValue)) * 100);
		},
		[minValue, maxValue],
	);

	// Set width of the range to decrease from the left side
	useEffect(() => {
		if (maxValRef.current) {
			const minPercent = getPercent(minVal);
			const maxPercent = getPercent(+maxValRef.current.value);

			if (range.current) {
				range.current.style.left = `${minPercent}%`;
				range.current.style.width = `${maxPercent - minPercent}%`;
			}
		}
	}, [minVal, getPercent]);

	// Set width of the range to decrease from the right side
	useEffect(() => {
		if (minValRef.current) {
			const minPercent = getPercent(+minValRef.current.value);
			const maxPercent = getPercent(maxVal);

			if (range.current) {
				range.current.style.width = `${maxPercent - minPercent}%`;
			}
		}
	}, [maxVal, getPercent]);

	// Get min and max values when their state changes
	useEffect(() => {
		if (onChange) {
			onChange({ min: debouncedMinVal, max: debouncedMaxVal });
		}
		if (putSelectedValueInUrlQuery) {
			////////////////////////////////////////////////////
			// Add values as URL search params
			////////////////////////////////////////////////////
			if (putSelectedValueInUrlQuery && debouncedMinVal && debouncedMinVal !== minValue) {
				addURLSearchParams(putSelectedValueInUrlQuery + 'Min', String(debouncedMinVal));
			}
			if (putSelectedValueInUrlQuery && debouncedMaxVal && debouncedMaxVal !== maxValue) {
				addURLSearchParams(putSelectedValueInUrlQuery + 'Max', String(debouncedMaxVal));
			}
			if (putSelectedValueInUrlQuery && debouncedMinVal === minValue && !firstLoad) {
				deleteURLSearchParams([putSelectedValueInUrlQuery + 'Min']);
			}
			if (putSelectedValueInUrlQuery && debouncedMaxVal === maxValue && !firstLoad) {
				deleteURLSearchParams([putSelectedValueInUrlQuery + 'Max']);
			}
			firstLoad = false;
			////////////////////////////////////////////////////
		}
	}, [debouncedMinVal, debouncedMaxVal, onChange]);

	useEffect(() => {
		if (resetAppliedFilters) {
			setMaxVal(maxValue);
			setMinVal(minValue);
			setResetAppliedFilters(false);
		}
	}, [resetAppliedFilters]);

	const valueString = (isInputField?: boolean) => {
		if (isInputField && minVal === minValue && maxVal === maxValue) {
			return '';
		} else {
			return `${customMinDisplayValueFunction ? customMinDisplayValueFunction(minVal) : minVal} - ${
				customMaxDisplayValueFunction ? customMaxDisplayValueFunction(maxVal) : maxVal
			}`;
		}
	};

	const handleClearSelectedValue = (e: React.MouseEvent) => {
		e.stopPropagation();
		e.preventDefault();
		setMinVal(minValue);
		setMaxVal(maxValue);
		if (readURLSearchParams(putSelectedValueInUrlQuery + 'Min')) {
			deleteURLSearchParams([putSelectedValueInUrlQuery + 'Min']);
		}
		if (readURLSearchParams(putSelectedValueInUrlQuery + 'Max')) {
			deleteURLSearchParams([putSelectedValueInUrlQuery + 'Max']);
		}
	};

	return (
		<div
			className={`fieldGroup ${className ? className : ''} ${displayOnlyRangeSlider ? 'inlineSlider' : ''}`}
			onClick={() => setOpen(!open)}
			ref={fieldRef}
		>
			<label
				className={`inputHolder cursor-text-hover ${highlightWhenValueSet && valueString(true) ? 'highlight' : ''}`}
			>
				<input
					{...input}
					type="text"
					value={valueString(true)}
					className={classNames('inputField thumb-1', {
						hasIconRight: iconName,
					})}
					onChange={() => null}
				/>
				{iconName
					? minVal !== minValue || maxVal !== maxValue
						? renderIcon('cancel_search', undefined, undefined, 'clearFieldIcon pointer', handleClearSelectedValue)
						: renderIcon(iconName, customIconSizeWidth || 22, customIconSizeHeight || 22, iconClassName, iconAction)
					: null}
				<div
					className="rangeDropdown"
					style={{ display: open ? 'block' : displayOnlyRangeSlider ? 'block' : 'none' }}
					onClick={(e) => {
						e.stopPropagation(), e.preventDefault();
					}}
				>
					<p className="rangeResult">{valueString()}</p>
					<input
						type="range"
						min={minValue}
						max={maxValue}
						value={minVal}
						ref={minValRef}
						onChange={(e) => {
							const value = Math.min(+e.target.value, maxVal - 1);
							setMinVal(value);
							e.target.value = value.toString();
						}}
						className={classNames('thumb thumb-1', { 'thumb-3': minVal > maxValue - minValue })}
					/>
					<input
						type="range"
						min={minValue}
						max={maxValue}
						value={maxVal}
						ref={maxValRef}
						onChange={(e) => {
							const value = Math.max(+e.target.value, minVal + 1);
							setMaxVal(value);
							e.target.value = value.toString();
						}}
						className="thumb thumb-2"
					/>
					<div className="slider">
						<div className="slider__track" />
						<div ref={range} className="slider__range" />
					</div>
				</div>
			</label>
		</div>
	);
};
const RangeSliderField = withRouter(RangeSliderFieldW);
export default RangeSliderField;
